import React, { useState, useEffect } from 'react';
import api from '../api';

const CustomQuizCreator = ({ onQuizCreated, onClose }) => {
  const [availableQuizzes, setAvailableQuizzes] = useState([]);
  const [selectedQuizzes, setSelectedQuizzes] = useState({});
  const [customTitle, setCustomTitle] = useState('');
  const [timeLimit, setTimeLimit] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [totalQuestions, setTotalQuestions] = useState(0);

  useEffect(() => {
    fetchAvailableQuizzes();
  }, []);

  useEffect(() => {
    // Calculate total questions from selected quizzes
    const total = Object.values(selectedQuizzes).reduce((sum, count) => sum + count, 0);
    setTotalQuestions(total);
  }, [selectedQuizzes]);

  const fetchAvailableQuizzes = async () => {
    try {
      setLoading(true);
      const res = await api.get('/quizzes');
      console.log('Quiz fetch response:', res.data);

      if (res.data.success && res.data.quizzes) {
        const quizData = res.data.quizzes.data || [];
        // Filter out custom quizzes
        const originalQuizzes = quizData.filter(quiz => !quiz.isCustom);
        console.log('Available quizzes:', originalQuizzes);
        setAvailableQuizzes(originalQuizzes);

        if (originalQuizzes.length === 0) {
          setError('Không có bài tập nào khả dụng để tạo bài tập tùy chỉnh');
        }
      } else {
        setError('Không thể tải danh sách bài tập');
      }
    } catch (err) {
      console.error('Error fetching quizzes:', err);
      setError('Lỗi khi tải danh sách bài tập: ' + (err.response?.data?.msg || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleQuizQuestionCountChange = (quizId, count) => {
    const quiz = availableQuizzes.find(q => q._id === quizId);
    const maxQuestions = quiz?.questions?.length || 0;
    const validCount = Math.min(Math.max(0, parseInt(count) || 0), maxQuestions);

    setSelectedQuizzes(prev => {
      const updated = { ...prev };
      if (validCount > 0) {
        updated[quizId] = validCount;
      } else {
        delete updated[quizId];
      }
      return updated;
    });
  };

  const handleSelectAllQuestions = (quizId) => {
    const quiz = availableQuizzes.find(q => q._id === quizId);
    const maxQuestions = quiz?.questions?.length || 0;
    handleQuizQuestionCountChange(quizId, maxQuestions);
  };

  const handleCreateCustomQuiz = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const selectedQuizIds = Object.keys(selectedQuizzes);
    if (selectedQuizIds.length === 0) {
      setError('Vui lòng chọn ít nhất một bài tập');
      return;
    }

    if (totalQuestions === 0) {
      setError('Vui lòng chọn ít nhất một câu hỏi');
      return;
    }

    setCreating(true);

    try {
      const res = await api.post('/quizzes/create-custom', {
        sourceQuizIds: selectedQuizIds,
        questionCounts: selectedQuizzes, // Send question counts per quiz
        questionCount: totalQuestions,
        title: customTitle.trim() || undefined,
        timeLimit: timeLimit ? parseInt(timeLimit) : undefined
      });

      if (res.data.success) {
        setSuccess(`✅ Đã tạo bài tập tùy chỉnh "${res.data.quiz.title}" thành công!`);

        // Reset form
        setSelectedQuizzes({});
        setCustomTitle('');
        setTimeLimit('');

        // Call parent callback
        if (onQuizCreated) {
          onQuizCreated(res.data.quiz);
        }
      }
    } catch (err) {
      console.error('Error creating custom quiz:', err);
      setError(
        err.response?.data?.errors?.[0]?.msg ||
        err.response?.data?.msg ||
        'Không thể tạo bài tập tùy chỉnh'
      );
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Tạo Bài Tập Tùy Chỉnh</h2>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-800 text-sm font-medium">{success}</p>
        </div>
      )}

      <form onSubmit={handleCreateCustomQuiz} className="space-y-6">
        {/* Quiz Selection with Individual Question Counts */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            Chọn Bài Tập và Số Lượng Câu Hỏi
          </label>
          {availableQuizzes.length === 0 ? (
            <div className="text-center p-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <svg className="mx-auto h-12 w-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-gray-600 font-medium">Không có bài tập nào</p>
              <p className="text-sm text-gray-500 mt-1">Giáo viên chưa tạo bài tập cho lớp của bạn</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto border border-gray-200 rounded-lg p-4 bg-gray-50">
              {availableQuizzes.map(quiz => {
                const questionCount = quiz.questions?.length || 0;
                const selectedCount = selectedQuizzes[quiz._id] || 0;
                const isSelected = selectedCount > 0;

                return (
                  <div
                    key={quiz._id}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      isSelected
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{quiz.title}</h3>
                        {quiz.description && (
                          <p className="text-sm text-gray-600 mt-1">{quiz.description}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          Tổng số câu hỏi: <span className="font-semibold">{questionCount}</span>
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                        Số câu hỏi:
                      </label>
                      <input
                        type="number"
                        min="0"
                        max={questionCount}
                        value={selectedCount}
                        onChange={(e) => handleQuizQuestionCountChange(quiz._id, e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="0"
                      />
                      <button
                        type="button"
                        onClick={() => handleSelectAllQuestions(quiz._id)}
                        className="px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-100 rounded-md hover:bg-indigo-200 transition-colors whitespace-nowrap"
                      >
                        Chọn tất cả
                      </button>
                    </div>

                    {selectedCount > 0 && (
                      <div className="mt-2 text-sm text-indigo-700 bg-indigo-100 px-3 py-1 rounded-md inline-block">
                        ✓ Đã chọn {selectedCount} câu hỏi
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {totalQuestions > 0 && (
            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm font-semibold text-green-800">
                Tổng cộng: {totalQuestions} câu hỏi từ {Object.keys(selectedQuizzes).length} bài tập
              </p>
            </div>
          )}
        </div>

        {/* Custom Title (Optional) */}
        <div>
          <label htmlFor="customTitle" className="block text-sm font-semibold text-gray-700 mb-2">
            Tên Bài Tập (Không bắt buộc)
          </label>
          <input
            type="text"
            id="customTitle"
            value={customTitle}
            onChange={(e) => setCustomTitle(e.target.value)}
            placeholder="Để trống để tự động tạo tên"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>

        {/* Time Limit (Optional) */}
        <div>
          <label htmlFor="timeLimit" className="block text-sm font-semibold text-gray-700 mb-2">
            Thời Gian Làm Bài (phút, không bắt buộc)
          </label>
          <input
            type="number"
            id="timeLimit"
            value={timeLimit}
            onChange={(e) => setTimeLimit(e.target.value)}
            min="1"
            placeholder="Không giới hạn"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>

        {/* Submit Button */}
        <div className="flex space-x-4 pt-4 border-t">
          <button
            type="submit"
            disabled={creating || Object.keys(selectedQuizzes).length === 0}
            className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md"
          >
            {creating ? 'Đang tạo...' : 'Tạo Bài Tập Tùy Chỉnh'}
          </button>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border-2 border-gray-300 rounded-lg text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
            >
              Hủy
            </button>
          )}
        </div>
      </form>

      {/* Info Box */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h3 className="font-semibold text-blue-900 mb-2 flex items-center">
          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          Hướng dẫn:
        </h3>
        <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
          <li>Chọn số lượng câu hỏi bạn muốn từ mỗi bài tập</li>
          <li>Câu hỏi sẽ được chọn ngẫu nhiên và xáo trộn</li>
          <li>Bạn có thể làm bài tập tùy chỉnh nhiều lần</li>
          <li>Điểm cao nhất sẽ được lưu lại</li>
        </ul>
      </div>
    </div>
  );
};

export default CustomQuizCreator;
